<template>
    <div class="gseedContainer">
        <div class="titleContainer">
            <div style="display: flex; justify-content: center; align-items: center">
                <div class="common-title">{{ "녹색건축인증 (G-SEED)" }}</div>
                <span
                    style="font-size: 14px; margin-left: 10px; cursor: pointer"
                    @click="() => $refs.gseedModal.show()"
                >
                    <i class="fa fa-question-circle"></i>
                </span>
            </div>
        </div>

        <template v-if="viewModel && viewModel.hasData">
            <div class="summaryRow">
                <div class="gradeBadge">
                    <span class="gradeBadgeLabel">등급</span>
                    <span class="gradeBadgeValue">{{ viewModel.summary.rating }}</span>
                </div>
                <div class="gradeBadge">
                    <span class="gradeBadgeLabel">종합 점수</span>
                    <span class="gradeBadgeValue">{{ formatScore(viewModel.summary.totalScore) }}</span>
                </div>
            </div>

            <div ref="chart" class="chartArea"></div>
        </template>

        <div v-else class="emptyState">
            <i class="fas fa-leaf"></i>
            <span>G-SEED 데이터가 없습니다.</span>
        </div>

        <b-modal ref="gseedModal" hide-footer size="lg" @hidden="() => $refs.gseedModal.hide()">
            <template #modal-header>
                <div class="d-flex justify-content-between align-items-center w-100">
                    <h5 class="mb-0">녹색건축인증(G-SEED) 안내</h5>
                    <span class="setting-icon" @click="() => $refs.gseedModal.hide()">
                        <i class="fas fa-times"></i>
                    </span>
                </div>
            </template>
            <template #default>
                <p class="mb-2 gseed-modal-desc">
                    G-SEED는 건축물의 친환경 성능을 7개 대분류로 평가하는 녹색건축 인증제도입니다. 아래 표는
                    <strong>구분별 인증 등급 기준 점수</strong>(이상)입니다.
                </p>
                <div class="gseed-criteria-table-wrap">
                    <table class="table table-bordered text-center mb-0 gseed-criteria-table">
                        <thead class="thead-light">
                            <tr>
                                <th colspan="2">구분</th>
                                <th>최우수<br><span class="grade-sub">(그린1등급)</span></th>
                                <th>우수<br><span class="grade-sub">(그린2등급)</span></th>
                                <th>우량<br><span class="grade-sub">(그린3등급)</span></th>
                                <th>일반<br><span class="grade-sub">(그린4등급)</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in gseedCertTableRows" :key="row.key">
                                <th
                                    v-if="row.showSection"
                                    class="cell-group"
                                    :rowspan="row.sectionRowspan"
                                    scope="rowgroup"
                                >
                                    {{ row.sectionName }}
                                </th>
                                <td class="cell-type">{{ row.label }}</td>
                                <td v-for="(cell, colIdx) in row.thresholds" :key="colIdx">
                                    {{ cell }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script>
import * as echarts from "echarts";

/** G-SEED 인증 등급 기준 점수 (이상) — 안내 모달용 */
const GSEED_CERT_CRITERIA = [
    {
        name: "신축",
        rows: [
            { label: "주거용", thresholds: ["74점 이상", "66점 이상", "58점 이상", "50점 이상"] },
            { label: "단독주택", thresholds: ["74점 이상", "66점 이상", "58점 이상", "50점 이상"] },
            { label: "비주거용", thresholds: ["80점 이상", "70점 이상", "60점 이상", "50점 이상"] },
        ],
    },
    {
        name: "기존",
        rows: [
            { label: "주거용", thresholds: ["69점 이상", "61점 이상", "53점 이상", "45점 이상"] },
            { label: "비주거용", thresholds: ["75점 이상", "65점 이상", "55점 이상", "45점 이상"] },
        ],
    },
    {
        name: "그린리모델링",
        rows: [
            { label: "주거용", thresholds: ["69점 이상", "61점 이상", "53점 이상", "45점 이상"] },
            { label: "비주거용", thresholds: ["75점 이상", "65점 이상", "55점 이상", "45점 이상"] },
        ],
    },
];

const BAR_HIGHLIGHT = ["#2E7D32", "#43A047"];
const BAR_DEFAULT = ["#90A4AE", "#B0BEC5"];

export default {
    name: "GseedGradeChart",
    props: {
        viewModel: {
            type: Object,
            default: () => null,
        },
    },
    data() {
        return {
            chart: null,
            gseedCertCriteria: GSEED_CERT_CRITERIA,
        };
    },
    computed: {
        gseedCertTableRows() {
            const rows = [];
            this.gseedCertCriteria.forEach((section) => {
                section.rows.forEach((row, rowIdx) => {
                    rows.push({
                        key: `${section.name}-${row.label}`,
                        sectionName: section.name,
                        sectionRowspan: section.rows.length,
                        showSection: rowIdx === 0,
                        label: row.label,
                        thresholds: row.thresholds,
                    });
                });
            });
            return rows;
        },
    },
    watch: {
        viewModel: {
            handler() {
                this.$nextTick(() => this.renderChart());
            },
            deep: true,
            immediate: true,
        },
    },
    mounted() {
        window.addEventListener("resize", this.onResize);
        this.$nextTick(() => this.renderChart());
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.onResize);
        this.disposeChart();
    },
    methods: {
        formatScore(v) {
            if (v == null || v === "") return "—";
            return `${v}점`;
        },
        onResize() {
            if (this.chart) this.chart.resize();
        },
        disposeChart() {
            if (this.chart) {
                this.chart.dispose();
                this.chart = null;
            }
        },
        resizeChart() {
            this.onResize();
        },
        cleanupChart() {
            this.disposeChart();
        },
        barColor(highlight) {
            const [from, to] = highlight ? BAR_HIGHLIGHT : BAR_DEFAULT;
            return new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: from },
                { offset: 1, color: to },
            ]);
        },
        buildOption() {
            const categories = this.viewModel?.categories || [];
            const labels = categories.map((c) => c.shortName || c.name);
            const scores = categories.map((c) => c.score ?? 0);
            const maxScores = categories.map((c) => c.maxScore ?? 100);
            const trackData = maxScores.map((m) => m);

            return {
                tooltip: {
                    trigger: "axis",
                    axisPointer: { type: "shadow" },
                    confine: true,
                    backgroundColor: "rgba(255,255,255,0.96)",
                    borderColor: "rgba(0,0,0,0.06)",
                    borderWidth: 1,
                    textStyle: { color: "#37474F", fontSize: 11 },
                    formatter: (params) => {
                        const idx = params[0]?.dataIndex;
                        const cat = categories[idx];
                        if (!cat) return "";
                        return `<div style="font-weight:600">${cat.name}</div>
                            <div>점수 <b>${cat.score}</b> / ${cat.maxScore}</div>`;
                    },
                },
                grid: { top: 4, left: 2, right: 12, bottom: 2, containLabel: true },
                xAxis: {
                    type: "value",
                    max: 100,
                    show: false,
                },
                yAxis: {
                    type: "category",
                    inverse: true,
                    data: labels,
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: {
                        fontSize: 11,
                        fontWeight: 600,
                        color: (value, index) => (categories[index]?.highlight ? "#2E7D32" : "#546E7A"),
                    },
                },
                series: [
                    {
                        name: "만점",
                        type: "bar",
                        data: trackData,
                        barWidth: "72%",
                        barGap: "-100%",
                        silent: true,
                        z: 1,
                        itemStyle: {
                            color: "rgba(227, 242, 253, 0.55)",
                            borderRadius: [0, 6, 6, 0],
                        },
                    },
                    {
                        name: "점수",
                        type: "bar",
                        data: scores.map((v, i) => ({
                            value: v,
                            itemStyle: {
                                color: this.barColor(categories[i]?.highlight),
                                borderRadius: [0, 6, 6, 0],
                                shadowBlur: categories[i]?.highlight ? 6 : 0,
                                shadowColor: "rgba(46, 125, 50, 0.35)",
                            },
                        })),
                        barWidth: "72%",
                        z: 10,
                        label: {
                            show: true,
                            position: "right",
                            formatter: "{c}",
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#37474F",
                        },
                    },
                ],
            };
        },
        renderChart() {
            const el = this.$refs.chart;
            if (!el || !this.viewModel?.hasData) {
                this.disposeChart();
                return;
            }
            if (!this.chart) {
                this.chart = echarts.init(el);
            }
            this.chart.setOption(this.buildOption(), true);
            this.$nextTick(() => this.chart?.resize());
        },
    },
};
</script>

<style scoped>
.gseedContainer {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
}
.titleContainer {
    flex-shrink: 0;
    margin-bottom: 2px;
    font-size: 18px;
    color: #4d4d4d;
    font-weight: 800;
    display: flex;
    justify-content: center;
    align-items: center;
}
.summaryRow {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin: 6px 4px 4px;
    flex-shrink: 0;
    width: 100%;
}
.gradeBadge {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 56px;
    padding: 6px 12px;
    white-space: nowrap;
    border-radius: 10px;
    background: linear-gradient(145deg, #2e7d32, #43a047);
    color: #fff;
    box-shadow: 0 4px 12px rgba(46, 125, 50, 0.35);
}
.gradeBadgeLabel {
    font-size: 9px;
    opacity: 0.9;
    font-weight: 600;
}
.gradeBadgeValue {
    font-size: 14px;
    font-weight: 800;
    line-height: 1.2;
}
.chartArea {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    margin-top: -4px;
}
.emptyState {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #90a4ae;
    font-size: 0.85rem;
}
.emptyState i {
    font-size: 1.5rem;
}
.gseed-modal-desc {
    font-size: 0.9rem;
    color: #546e7a;
}
.gseed-criteria-table-wrap {
    overflow-x: auto;
}
.gseed-criteria-table {
    font-size: 0.8rem;
    margin-bottom: 0;
}
.gseed-criteria-table th,
.gseed-criteria-table td {
    vertical-align: middle;
    padding: 0.35rem 0.4rem;
}
.gseed-criteria-table .grade-sub {
    font-size: 0.72rem;
    font-weight: 500;
}
.gseed-criteria-table .cell-group {
    background-color: #f5f5f5;
    font-weight: 700;
    white-space: nowrap;
}
.gseed-criteria-table .cell-type {
    white-space: nowrap;
    text-align: left;
    padding-left: 0.6rem;
}
</style>
